import { formatMaintenanceWorks, MaintenanceWork } from './MaintenanceWork.model';

export enum L3ServiceStatus {
  Pending = 'PENDING',
  Ready = 'READY',
  Unavailable = 'UNAVAILABLE',
  Available = 'AVAILABLE',
  Archived = 'ARCHIVED'
}

export enum Preference {
  Default = 'DEFAULT',
  Favourite = 'FAVOURITE'
}

export interface ServiceStatusColor {
  background: string;
  color: string;
}

export interface L3ServiceApiModel {
  itemId: number;
  name: string;
  type: string;
  address: string;
  status: L3ServiceStatus;
  preference?: Preference;
  orderNumber: number;
  throughput?: number;
  device?: Device;
  maintenanceWorks: MaintenanceWork[];
  notifications: Notification[];
}

export type L3ServiceViewModel = Omit<L3ServiceApiModel, 'throughput' | 'notifications'> & {
  throughputFormatted: string;
  notificationsQuantity: number;
  formattedMaintenanceWorks: string | undefined;
};

export interface Device {
  type: string;
}

export interface Notification {
  content: string;
}

export const mapL3ServicesApiToViewModel = (services: L3ServiceApiModel[]): L3ServiceViewModel[] =>
  services.map(({ notifications, ...service }) => ({
    ...service,
    notificationsQuantity: notifications?.length ?? 0,
    throughputFormatted: formatThroughput(service.throughput),
    formattedMaintenanceWorks: formatMaintenanceWorks(service.maintenanceWorks),
  }));

export const serviceStatusColorMap: { [P in L3ServiceStatus]: string } = {
  [L3ServiceStatus.Pending]: '#FB923C',
  [L3ServiceStatus.Archived]: '#D1D1D1',
  [L3ServiceStatus.Ready]: '#D887FE',
  [L3ServiceStatus.Unavailable]: '#FF5757',
  [L3ServiceStatus.Available]: '#73D18D',
};

export const getL3ServiceStatusColor = (status: L3ServiceStatus): ServiceStatusColor => {
  switch (status) {
    case L3ServiceStatus.Unavailable:
      return { background: 'rgb(255, 87, 87)', color: 'white' };
    default:
      return { background: serviceStatusColorMap[status], color: 'inherit' };
  }
};

export const formatThroughput = (value?: number): string => {
  const valueUnitMap = new Map<number, string>([
    [0, ''],
    [1000, 'K'],
    [1000000, 'M'],
    [1000000000, 'G'],
  ]);
  if (typeof value === 'number') {
    let result = `${value}`;
    valueUnitMap.forEach((subUnit, edgeValue) => {
      if (value > edgeValue) {
        result = `${Number((value / Math.max(edgeValue, 1)).toFixed(Math.min(edgeValue, 3)))} ${subUnit}bps`;
      }
    });
    return result;
  } else {
    return '';
  }
};